/**
 * Follow-ups — the queue registry (redesign Phase 0, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── WHAT IT REPLACES ───────────────────────────────────────────────────────────────────────────
 * AdminTable.vue answers "which queue is this?" with FIVE separate `switch (this.extra.detailPageName)`
 * blocks — getOriginalDataIndex (pageIamOn + unique-row field), getTaskTypeID, createFollowup (the
 * drawer query), getAssignedToEmployeeID and filteredEmployees (the responsible-employee setting).
 * Every value below is copied from those blocks, and cypress/node-tests/followups-queues.test.js
 * reads them back OUT OF AdminTable.vue's code (comments stripped) and fails on any difference — so
 * AdminTable.vue, which is frozen for the redesign, can never drift away from this file unnoticed.
 *
 * ── COLUMN SETS ────────────────────────────────────────────────────────────────────────────────
 * The desktop and tablet sets are the Claude Design export's COLS / TCOLS (Follow-ups.dc.html),
 * keyed by the export's column names. The phone layout is a card list, not columns.
 * Two departures, both deliberate:
 *   • 'rem' (Auto Reminders Sent) is dropped when the office has not paid for reminders — the
 *     legacy pages only add the AutoRemindersSent column when APPT_REMINDER_PAID_FOR is 'TRUE'.
 *   • Processed pre-auths are not in the export at all. They get the pre-auth set plus 'received',
 *     because the legacy table shows Date Received there and dropping it would lose information.
 */

/** The export's breakpoints: phone below 640, tablet below 900. Mirrored in _tokens.scss. */
export const BREAKPOINTS = { phone: 640, tablet: 900 };

/**
 * Every column the design draws. `sortable` = the export gives it a sort key; `right` = numeric.
 * The recall columns' MINIMUMS are below the export's (Phase 2, 2026-10-02): the export assumed a
 * 1280 window with no sidebar; inside the app a 1280 window leaves the card ~1120px, and the
 * export's minimums (≈1234px with gaps) pushed the Follow-up column out of view. The spare width
 * goes mostly to Patient and Status (larger `fr`), the two text columns that truncate first.
 */
export const COLUMN_DEFS = {
  check: { label: '', width: '28px', sortable: false },
  patient: { label: 'Patient', width: 'minmax(170px,2.2fr)', sortable: true },
  guarantor: { label: 'Guarantor', width: 'minmax(200px,1.6fr)', sortable: true },
  // Polish (2026-10-03): column headers never wrap (FollowupsTable `white-space: nowrap`), so each
  // minimum fits its header + sort icon — "Recall due" / "Last hygienist" wrapped at 1280. `head` is
  // a shorter header-only label; the phone card label and the Sort select keep `label`.
  recall: { label: 'Recall due', width: 'minmax(100px,.9fr)', sortable: true },
  hyg: { label: 'Last hygienist', head: 'Hygienist', width: 'minmax(90px,.7fr)', sortable: true },
  ins: { label: 'Insurance', width: 'minmax(116px,1.2fr)', sortable: true },
  rem: { label: 'Reminders', width: '96px', sortable: true },
  status: { label: 'Status', width: 'minmax(124px,1.3fr)', sortable: true },
  att: { label: 'Attempts', width: '88px', sortable: true },
  fu: { label: 'Follow-up', width: '176px', sortable: true },
  remove: { label: '', width: '34px', sortable: false },
  txdate: { label: 'Tx planned', width: 'minmax(104px,.85fr)', sortable: true },
  utins: { label: 'Insurance', width: 'minmax(170px,1.4fr)', sortable: true },
  // 104px, not 120 (Phase 3): the Unscheduled List's tablet set (+ the remove column) overflowed an
  // 820px window by a few px; "$12,380.00" still fits. Headers no longer wrap (2026-10-03), so the
  // header says "Tx amount".
  txamt: { label: 'Tx plan amount', head: 'Tx amount', width: 'minmax(104px,1fr)', sortable: true, right: true },
  sent: { label: 'Sent', width: 'minmax(122px,1fr)', sortable: true },
  service: { label: 'Date of service', head: 'Service date', width: 'minmax(112px,.85fr)', sortable: true },
  received: { label: 'Received', width: 'minmax(108px,.85fr)', sortable: true },
  carrier: { label: 'Carrier', width: 'minmax(150px,1.25fr)', sortable: true },
  claimfee: { label: 'Claim fee', width: 'minmax(110px,.9fr)', sortable: true, right: true },
  balance: { label: 'Balance', width: 'minmax(110px,.9fr)', sortable: true, right: true },
  lastvisit: { label: 'Last visit', width: 'minmax(104px,.85fr)', sortable: true },
  billing: { label: 'Billing type', width: 'minmax(130px,1fr)', sortable: true },
  brokendate: { label: 'Broken on', width: 'minmax(110px,.9fr)', sortable: true },
  proc: { label: 'Procedure / adjustment', width: 'minmax(200px,1.6fr)', sortable: true },
  fee: { label: 'Fee', width: 'minmax(84px,.6fr)', sortable: true, right: true },
  // Patient Profile (Phase 5): which queue a row belongs to, and that queue's one-line facts.
  // Minimums below the export's (180 / 220): the profile's half-width column must fit the
  // tablet set (queue, detail, status, fu) from ~672px without a sideways scroll.
  queue: { label: 'Queue', width: 'minmax(140px,1fr)', sortable: true },
  detail: { label: 'Details', width: 'minmax(160px,1.6fr)', sortable: false },
};

/**
 * Phase 4 (2026-10-02): inside the app an 820px window leaves the table card ~733px wide (the
 * sidebar and gutters), and the export's tablet sets for claims, pre-auths, balances and broken
 * appointments need 786–864px — the Follow-up column went behind a sideways scroll. Their tablet
 * sets drop ONE column each (claim fee; carrier, also on the unused processed pre-auths; last
 * visit; procedure — all still on desktop and in search). A phone card is not width-bound, so
 * those get a `phone` set = the export's TCOLS and the phone card keeps every field. A queue
 * without `phone` draws its tablet set on a phone.
 *
 * The seven queues. Field by field:
 *   detailPageName   AdminTable's `extra.detailPageName` — the key every legacy switch reads
 *   taskTypeID       getTaskTypeID()
 *   uniqueField      getOriginalDataIndex()'s uniqueID — how a row is found again after a save
 *   pageIamOn        getOriginalDataIndex()'s pageIamOn
 *   responsibleEmployeeSetting  getAssignedToEmployeeID() / filteredEmployees() — "id" or "id|id|…"
 *   drawerQuery      createFollowup()'s query: its `type`, and its keys IN THE LEGACY ORDER
 *   statusSet        which of the design's status vocabularies the drawer offers (patient/claim/balance)
 *   nameField        the row field the first column prints (Patient, or Guarantor for balances)
 *   showsAssignee    the follow-up cell shows "Assigned to" (legacy: only on /app/aging)
 */
export const QUEUES = [
  {
    key: 'overdueRecall',
    designKey: 'or',
    label: 'Overdue recall',
    title: 'Recall follow-up',
    detailPageName: 'OverdueRecallDetails',
    taskTypeID: 3,
    uniqueField: 'PatNum',
    pageIamOn: 'OVERDUE_RECALL',
    responsibleEmployeeSetting: 'OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID',
    drawerQuery: { type: 'OVERDUE_RECALL_DETAILS', keys: ['type', 'siteID', 'patientID', '_date', 'country'] },
    statusSet: 'patient',
    nameField: 'Patient',
    showsAssignee: false,
    listPageName: 'OverdueRecallList',
    columns: {
      desktop: ['check', 'patient', 'recall', 'hyg', 'ins', 'rem', 'status', 'att', 'fu'],
      tablet: ['check', 'patient', 'recall', 'status', 'fu'],
    },
    listColumns: {
      desktop: ['check', 'patient', 'recall', 'hyg', 'ins', 'rem', 'status', 'att', 'fu', 'remove'],
      tablet: ['check', 'patient', 'recall', 'status', 'fu', 'remove'],
    },
  },
  {
    key: 'unscheduledTreatment',
    designKey: 'ut',
    label: 'Unscheduled treatment',
    title: 'Unscheduled treatment follow-up',
    detailPageName: 'unscheduledPatientDetails',
    taskTypeID: 4,
    uniqueField: 'PatNum',
    pageIamOn: 'UNSCHEDULED_TREATMENT',
    responsibleEmployeeSetting: 'UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID',
    drawerQuery: { type: 'UNSCHEDULED_TREATMENT_DETAILS', keys: ['type', 'siteID', 'patientID', '_date', 'country'] },
    statusSet: 'patient',
    nameField: 'Patient',
    showsAssignee: false,
    listPageName: 'unscheduledList',
    columns: {
      desktop: ['check', 'patient', 'txdate', 'utins', 'txamt', 'status', 'att', 'fu'],
      tablet: ['check', 'patient', 'txamt', 'status', 'fu'],
    },
    listColumns: {
      desktop: ['check', 'patient', 'txdate', 'utins', 'txamt', 'status', 'att', 'fu', 'remove'],
      tablet: ['check', 'patient', 'txamt', 'status', 'fu', 'remove'],
    },
  },
  {
    key: 'outstandingClaims',
    designKey: 'claims',
    label: 'Outstanding claim',
    title: 'Outstanding claim follow-up',
    detailPageName: 'AgingDetails',
    taskTypeID: 2,
    uniqueField: 'ClaimNum',
    pageIamOn: 'OUTSTANDING_CLAIMS',
    responsibleEmployeeSetting: 'OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID',
    drawerQuery: { type: 'AGING_DETAILS', keys: ['type', 'siteID', 'patientID', 'claimID', 'country'] },
    statusSet: 'claim',
    nameField: 'Patient',
    showsAssignee: true,
    listPageName: null,
    columns: {
      desktop: ['patient', 'sent', 'service', 'carrier', 'claimfee', 'status', 'att', 'fu'],
      tablet: ['patient', 'sent', 'status', 'fu'],
      phone: ['patient', 'sent', 'claimfee', 'status', 'fu'],
    },
    listColumns: null,
  },
  {
    key: 'outstandingPreAuths',
    designKey: 'preauth',
    label: 'Pre-authorization',
    title: 'Pre-authorization follow-up',
    detailPageName: 'OutstandingPreAuthDetails',
    taskTypeID: 5,
    uniqueField: 'ClaimNum',
    pageIamOn: 'OUTSTANDING_PREAUTH',
    responsibleEmployeeSetting: 'OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
    drawerQuery: { type: 'OUTSTANDING_PREAUTH_DETAILS', keys: ['type', 'siteID', 'patientID', 'claimID', '_date', 'country'] },
    statusSet: 'claim',
    nameField: 'Patient',
    showsAssignee: true,
    listPageName: null,
    columns: {
      desktop: ['patient', 'sent', 'carrier', 'status', 'att', 'fu'],
      tablet: ['patient', 'sent', 'status', 'fu'],
      phone: ['patient', 'sent', 'carrier', 'status', 'fu'],
    },
    listColumns: null,
  },
  {
    key: 'processedPreAuths',
    designKey: 'preauth',
    label: 'Processed pre-authorization',
    title: 'Processed pre-authorization follow-up',
    detailPageName: 'ProcessedPreAuthDetails',
    taskTypeID: 6,
    uniqueField: 'ClaimNum',
    pageIamOn: 'PROCESSED_PREAUTH',
    responsibleEmployeeSetting: 'PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
    // ⚠ Legacy sends NO claimID for a processed pre-auth (unlike the outstanding one) — kept as is.
    drawerQuery: { type: 'PROCESSED_PREAUTH_DETAILS', keys: ['type', 'siteID', 'patientID', '_date', 'country'] },
    statusSet: 'claim',
    nameField: 'Patient',
    showsAssignee: true,
    listPageName: null,
    columns: {
      desktop: ['patient', 'sent', 'received', 'carrier', 'status', 'att', 'fu'],
      tablet: ['patient', 'sent', 'status', 'fu'],
      phone: ['patient', 'sent', 'carrier', 'status', 'fu'],
    },
    listColumns: null,
  },
  {
    key: 'patientBalances',
    designKey: 'balances',
    label: 'Patient balance',
    title: 'Patient balance follow-up',
    detailPageName: 'PatientBalancesDetails',
    taskTypeID: 7,
    uniqueField: 'PatNum',
    pageIamOn: 'PATIENT_BALANCES',
    responsibleEmployeeSetting: 'PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID',
    drawerQuery: { type: 'PATIENT_BALANCES_DETAILS', keys: ['type', 'siteID', 'patientID', '_date', 'country'] },
    statusSet: 'balance',
    nameField: 'Guarantor',
    showsAssignee: true,
    listPageName: null,
    columns: {
      desktop: ['guarantor', 'balance', 'lastvisit', 'billing', 'status', 'att', 'fu'],
      tablet: ['guarantor', 'balance', 'status', 'fu'],
      phone: ['guarantor', 'balance', 'lastvisit', 'status', 'fu'],
    },
    listColumns: null,
  },
  {
    key: 'brokenAppointments',
    designKey: 'broken',
    label: 'Broken appointment',
    title: 'Broken appointment follow-up',
    detailPageName: 'BrokenAppointmentDetails',
    taskTypeID: 8,
    uniqueField: 'PatNum',
    pageIamOn: 'BROKEN_APPOINTMENT',
    responsibleEmployeeSetting: 'BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID',
    drawerQuery: {
      type: 'BROKEN_APPOINTMENT_DETAILS',
      keys: ['type', 'siteID', 'patientID', 'adjustments', 'codes', '_date', 'country'],
    },
    statusSet: 'patient',
    nameField: 'Patient',
    showsAssignee: false,
    listPageName: null,
    columns: {
      desktop: ['patient', 'brokendate', 'proc', 'fee', 'status', 'att', 'fu'],
      tablet: ['patient', 'brokendate', 'status', 'fu'],
      phone: ['patient', 'brokendate', 'proc', 'status', 'fu'],
    },
    listColumns: null,
  },
];

const BY_KEY = {};
const BY_DETAIL_PAGE = {};
const BY_TASK_TYPE = {};
QUEUES.forEach((q) => {
  BY_KEY[q.key] = q;
  BY_DETAIL_PAGE[q.detailPageName] = q;
  BY_TASK_TYPE[q.taskTypeID] = q;
});

export function queueByKey(key) {
  return BY_KEY[key] || null;
}

/** The legacy `extra.detailPageName` → its queue (exact, case-sensitive — 'unscheduledPatientDetails'). */
export function queueByDetailPageName(name) {
  return BY_DETAIL_PAGE[name] || null;
}

export function queueByTaskTypeID(id) {
  return BY_TASK_TYPE[Number(id)] || null;
}

/** "<siteID>-<PatNum>", or the row's own TeamioPatientID when it has no PatNum (getTeamioPatientID). */
export function teamioPatientIdOf(row, siteID) {
  const r = row || {};
  return r.PatNum ? `${siteID}-${r.PatNum}` : r.TeamioPatientID;
}

/**
 * The row's identity: the claim for claim queues, the patient for patient queues — what
 * getUniqueID() returns (minus its index fallback, which a keyed renderer must not use: an
 * index changes when a filter changes, and a save would then land on the wrong row).
 */
export function rowIdOf(queue, row, siteID) {
  const q = queue || {};
  const r = row || {};
  const id = q.uniqueField === 'ClaimNum' ? r.ClaimNum : teamioPatientIdOf(r, siteID);
  return id === null || id === undefined || id === '' ? null : String(id);
}

/**
 * The query object the follow-up drawer (CreateFollowup.vue) receives — same keys, same order,
 * same fallbacks as AdminTable.createFollowup(). `today` is 'YYYY-MM-DD' in the site's zone
 * (legacy: moment().format('YYYY-MM-DD')); the localStorage reads are passed in raw.
 */
export function buildDrawerQuery(queue, { siteID, teamioPatientID, claimNum, today, country, brokenApptAdjustments, brokenApptCodes } = {}) {
  if (!queue || !queue.drawerQuery) return undefined;
  const values = {
    type: queue.drawerQuery.type,
    siteID,
    patientID: teamioPatientID,
    claimID: claimNum,
    adjustments: brokenApptAdjustments || '',
    codes: brokenApptCodes || '',
    _date: today,
    country: country || 'USA',
  };
  const out = {};
  queue.drawerQuery.keys.forEach((k) => { out[k] = values[k]; });
  return out;
}

/**
 * Patient Profile's Follow-ups section (Phase 5, 2026-10-02) — ONE patient's follow-ups across
 * every queue. Not in QUEUES on purpose: it is not a queue, and every rule above (task type,
 * responsible employees, drawer query, the agreement with AdminTable) belongs to each ROW's own
 * queue, found by queueByTaskTypeID(row.TaskTypeID) — the same TaskTypeID → detail page mapping
 * Patient Profile uses (mixin.js getDetailPageName). Column set = the export's `profile`.
 */
export const PROFILE_QUEUE = {
  key: 'patientProfile',
  label: 'Follow-ups',
  title: 'Follow-ups',
  columns: {
    desktop: ['queue', 'detail', 'status', 'att', 'fu'],
    tablet: ['queue', 'detail', 'status', 'fu'],
    phone: ['queue', 'detail', 'status', 'att', 'fu'],
  },
  listColumns: null,
};

/**
 * Admin "Today" worklist (Admin redesign Phase A1, 2026-10-06) — every queue's open follow-ups in
 * one list (src/components/Followups/AdminWorklist.vue). Like PROFILE_QUEUE it is not a queue:
 * each ROW keeps its own queue (the item's queueKey) for its follow-up rules, popover, saves and
 * drawer. Column set = the design's "Today 3a": TYPE · PATIENT · DETAILS · FOLLOW-UP · ATTEMPTS.
 * The worklist draws ONE header over all its sections, with `heads` as the header text (the
 * COLUMN_DEFS labels stay what the other tables show). Desktop needs 818px — from 900px up that
 * fits, and columnsFor drops 'att' first if it ever does not; tablet (640–899) needs 718px. Phone
 * draws cards: the type chip and the patient head the card, then details, attempts, follow-up.
 */
export const DASHBOARD_QUEUE = {
  key: 'adminDashboard',
  label: 'Today',
  title: 'Today',
  columns: {
    desktop: ['queue', 'patient', 'detail', 'fu', 'att'],
    tablet: ['queue', 'patient', 'detail', 'fu'],
    phone: ['queue', 'patient', 'detail', 'att', 'fu'],
  },
  listColumns: null,
  heads: { queue: 'Type', patient: 'Patient', detail: 'Details', fu: 'Follow-up', att: 'Attempts' },
};

/**
 * A Patient Profile row's identity: its teamio.tasks TaskID. The profile rows carry no PatNum or
 * TeamioPatientID (main-service sql/patient-profile.js `upcomingFollowups`), and a patient can have
 * one task per queue, so the patient id cannot tell two rows apart — the task can.
 */
export function profileRowIdOf(row) {
  const id = row ? row.TaskID : null;
  return id === null || id === undefined || id === '' ? null : `task-${id}`;
}

/** The responsible-employee setting split into EmployeeIDs ("id" or "id|id|…"), as filteredEmployees does. */
export function responsibleEmployeeIds(queue, allSettings) {
  const row = queue && allSettings && allSettings[queue.responsibleEmployeeSetting];
  const raw = row ? row.PropertyValue : null;
  return raw ? String(raw).split('|') : [];
}

/** 'phone' | 'tablet' | 'desktop' for a viewport width, on the export's breakpoints. */
export function layoutFor(width) {
  const w = Number(width);
  if (Number.isFinite(w) && w < BREAKPOINTS.phone) return 'phone';
  if (Number.isFinite(w) && w < BREAKPOINTS.tablet) return 'tablet';
  return 'desktop';
}

/**
 * The column keys to draw. Phone draws cards, so it gets the tablet set (the card picks fields
 * from it) — or the queue's own `phone` set when it has one (Phase 4, below). `remindersPaidFor` false drops 'rem', as the legacy pages drop AutoRemindersSent.
 */
export function columnsFor(queue, { width, list, remindersPaidFor } = {}) {
  if (!queue) return [];
  const sets = list && queue.listColumns ? queue.listColumns : queue.columns;
  const layout = layoutFor(width);
  let cols = sets.tablet;
  if (layout === 'desktop') cols = sets.desktop;
  else if (layout === 'phone' && sets.phone) cols = sets.phone;
  cols = cols.filter(c => c !== 'rem' || remindersPaidFor === true);
  if (layout === 'desktop') cols = fitDesktopColumns(cols, sets.tablet, width);
  return cols;
}

/** FollowupsTable's grid: 12px between tracks, 18px padding each side; the card adds a 1px border each side. */
export const TABLE_GAP = 12;
export const TABLE_PAD_X = 36;
const CARD_BORDER_X = 2;

/** The smallest px a column's track can shrink to ('minmax(190px,1.6fr)' → 190, '84px' → 84). */
export function columnMinPx(col) {
  const def = COLUMN_DEFS[col];
  const w = def ? def.width : '';
  const m = /^minmax\(\s*(\d+(?:\.\d+)?)px/.exec(w) || /^(\d+(?:\.\d+)?)px$/.exec(w);
  return m ? Number(m[1]) : 0;
}

/** The narrowest the table can be with these columns before it scrolls sideways. */
export function tableMinWidth(cols) {
  const list = cols || [];
  return list.reduce((a, c) => a + columnMinPx(c), 0) + TABLE_GAP * Math.max(0, list.length - 1) + TABLE_PAD_X;
}

/**
 * Polish (2026-10-03): from 900px up a queue draws its desktop set, but most desktop sets need
 * 1000–1170px (overdue recall ~1100), so a 900–1100px container scrolled the table sideways and
 * hid the Follow-up column. Instead, desktop columns that are NOT in the tablet set are dropped,
 * in this order (least needed at a glance first), until the set fits the container. It never goes
 * below the tablet set, which fits 733px (followups-queues.test.js). A dropped column is still in
 * search and comes back on a wider container.
 */
export const NARROW_DROP_ORDER = [
  'hyg', 'service', 'received', 'billing', 'lastvisit', 'rem', 'att', 'ins',
  'carrier', 'claimfee', 'txdate', 'utins', 'fee', 'proc',
];

function fitDesktopColumns(cols, tabletSet, width) {
  const w = Number(width);
  if (!Number.isFinite(w) || w <= 0) return cols;
  const room = w - CARD_BORDER_X;
  const keep = tabletSet || [];
  let out = cols;
  for (let i = 0; i < NARROW_DROP_ORDER.length && tableMinWidth(out) > room; i += 1) {
    const c = NARROW_DROP_ORDER[i];
    if (keep.indexOf(c) === -1) out = out.filter(x => x !== c);
  }
  return out;
}
